import { SuggestionData } from 'ckeditor5-premium-features'
import { SuggestionDataMap } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackedChange } from 'models/changes'

const TIME_FORMATTERS = {
    TODAY_TIME_FORMATTER: new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }),
    DATE_TIME_FORMATTER: new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }),
}

/**
 * A utility function to build an array of suggestions from tracked changes.
 * It maps each tracked change to a suggestion data object using the provided function.
 * @param changes
 * @param createSuggestionDataFromTrackChange
 * @param from
 * @returns TrackedChange[]
 */
export const buildSuggestionArray = (
    changes: TrackedChange[],
    toSuggestionData: (value: TrackedChange, from?: string) => SuggestionData,
    from?: string
) => {
    return changes.reduce<
        {
            id: string
            createdAt: number
            suggestion: SuggestionData
        }[]
    >((arr, value) => {
        const suggestionData = toSuggestionData(value, from)
        if (!suggestionData.id) return arr

        arr.push({
            id: suggestionData.id,
            createdAt: suggestionData.createdAt?.getTime?.() ?? 0,
            suggestion: suggestionData,
        })
        return arr
    }, [])
}

/**
 *  Sorts a suggestion array by `createdAt` in descending order.
 * @param suggestionArray
 * @returns Ordered array of suggestions
 */
export const sortSuggestionArray = (
    suggestionArray: {
        id: string
        createdAt: number
        suggestion: SuggestionData
    }[]
) => {
    return suggestionArray.slice().sort((a, b) => b.createdAt - a.createdAt)
}

/**
 * Converts a sorted array into a record of SuggestionDataMap.
 * Each entry in the record contains the suggestion ID and an array of suggestions.
 * @param sortedArray - The sorted array of suggestions.
 * @returns A record where each key is a suggestion ID and the value is an object containing the ID and an array of suggestions.
 */
export const buildSuggestionRecord = (
    sortedArray: { id: string; suggestion: SuggestionData }[]
): Record<string, SuggestionDataMap> => {
    const record: Record<string, SuggestionDataMap> = {}
    for (const item of sortedArray) {
        record[item.id] = {
            id: item.id,
            suggestions: [item.suggestion],
        }
    }
    return record
}

/** Normalizes tracked changes from the server and identifies stale IDs to remove. */
export const normalizeTrackChanges = (
    incoming: TrackedChange[],
    existing: Record<string, TrackedChange>
): {
    trackChangesDataWithId: Record<string, TrackedChange>
    removeTrackChangesId: string[]
} => {
    const trackChangesDataWithId: Record<string, TrackedChange> = {}
    const removeTrackChangesId: string[] = []
    const newIdsByElement = new Map<string, string>()

    // Build a set of all new IDs and a map of elementId to newId
    for (const change of incoming) {
        const baseId = change.contentItemElementId + ':'
        const newId = baseId + change.status

        // skip unchanged
        if (existing[newId]?.status === change.status) continue

        trackChangesDataWithId[newId] = change
        newIdsByElement.set(change.contentItemElementId, newId)
    }

    for (const id of Object.keys(existing)) {
        const [elementId] = id.split(':')
        const newId = newIdsByElement.get(elementId)
        if (newId && id !== newId) {
            removeTrackChangesId.push(id)
        }
    }

    return { trackChangesDataWithId, removeTrackChangesId }
}

/**
 * Utility to cache today's date information to avoid repeated Date operations.
 */
const DateCache = (() => {
    let _cache: {
        date: string
        year: number
        month: number
        day: number
    } | null = null

    return {
        /**
         * Gets today's date information with caching to avoid repeated Date operations
         */
        getTodayInfo: () => {
            const now = new Date()
            const todayString = now.toDateString()

            // Use cache if it's still the same day
            if (_cache?.date === todayString) {
                // Return a copy to prevent external mutation
                return { ..._cache }
            }

            // Update cache
            _cache = {
                date: todayString,
                year: now.getFullYear(),
                month: now.getMonth(),
                day: now.getDate(),
            }

            return { ..._cache }
        },

        resetCache: () => {
            _cache = null
        },
    }
})()

/**
 * Resets the cached date information to ensure fresh calculations
 */
export const resetDateCache = () => {
    DateCache.resetCache()
}

/**
 * takes a timestamp string and formats it to show "Today, HH:MM:SS AM/PM" if the date is today.
 * @param timeStamp - The timestamp string to format
 * @returns Formatted timestamp string or empty string if invalid
 */
export const formatTimeStamp = (timeStamp: string): string => {
    if (!timeStamp?.trim()) return ''

    try {
        const date = new Date(timeStamp)

        if (isNaN(date.getTime())) return timeStamp

        const today = DateCache.getTodayInfo()

        const isToday =
            date.getFullYear() === today.year &&
            date.getMonth() === today.month &&
            date.getDate() === today.day

        const formattedDate = isToday
            ? `Today ${TIME_FORMATTERS.TODAY_TIME_FORMATTER.format(date)}`
            : TIME_FORMATTERS.DATE_TIME_FORMATTER.format(date).replace(',', '')

        return formattedDate
    } catch {
        return typeof timeStamp === 'string'
            ? timeStamp.replace(/,/, '').replace(/:\d{2}\s/, ' ')
            : timeStamp
    }
}
